import { useState, ReactNode, KeyboardEvent } from 'react';

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  showDropdownIcon?: boolean;
  error?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  readOnly?: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  inputId?: string;
  ariaDescribedBy?: string;
}

export function Dropdown({
  value,
  onChange,
  placeholder,
  className = '',
  showDropdownIcon = true,
  error = false,
  disabled = false,
  children,
  readOnly = false,
  onBlur,
  onKeyDown,
  inputId,
  ariaDescribedBy,
}: DropdownProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          id={inputId}
          className={`w-full min-h-[2.75rem] px-4 py-2 font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] rounded-2xl border transition-colors placeholder:text-secondary/40 focus:outline-none ${
            error
              ? 'border-[#F04438] bg-[#FEE4E2]'
              : 'border-primary/20 bg-[#F0F5F7] focus:border-primary focus:ring-2 focus:ring-primary/10'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          value={value}
          onChange={e => !readOnly && onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => !disabled && setShowSuggestions(true)}
          onBlur={() => {
            onBlur?.();
            setTimeout(() => setShowSuggestions(false), 100);
          }}
          onKeyDown={onKeyDown}
          style={{ paddingRight: showDropdownIcon ? '2.5rem' : undefined }}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={error || undefined}
          aria-describedby={ariaDescribedBy}
        />
        {showDropdownIcon && (
          <span
            data-testid="dropdown-icon"
            className="absolute right-3 inset-y-0 flex items-center pointer-events-none"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 8L10 13L15 8"
                stroke="#8CA2B3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </div>
      {showSuggestions && children && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-primary/15 max-h-48 overflow-y-auto rounded-2xl shadow-[0_8px_24px_rgba(13,45,57,0.12)]">
          {children}
        </ul>
      )}
    </div>
  );
}
